/**
 * Auth Loading Component
 *
 * Full-page loading state shown while authentication status is being determined.
 * Used in protected routes while waiting for auth check.
 *
 * @layer features
 * @slice auth
 * @segment ui
 * @example
 * function AuthenticatedLayout() {
 *   const { isLoading } = useAuth();
 *   if (isLoading) return <AuthLoading />;
 *   return <Outlet />;
 * }
 *
 * @added DOC-0074 (2025-12-19)
 * @updated DOC-0094 (2025-12-26) - Migrated to FSD features/auth
 */

import { useTranslation } from 'react-i18next';
import { Container, Spinner, Stack, Text } from '@shared/ui';

/**
 * Props for AuthLoading component
 */
interface AuthLoadingProps {
  /** Optional custom message (defaults to i18n key) */
  message?: string;
}

/**
 * Full-page loading spinner for auth state
 *
 * Accessible with aria-busy and aria-label attributes.
 * Centers spinner vertically and horizontally.
 */
export function AuthLoading({ message }: AuthLoadingProps) {
  const { t } = useTranslation();

  return (
    <Container
      size="sm"
      className="flex min-h-screen items-center justify-center py-10"
      role="status"
      aria-busy="true"
      aria-label={t('common.loading')}
    >
      <Stack gap="md" className="items-center">
        <Spinner size="lg" aria-hidden="true" />
        <Text size="sm" color="muted">
          {message || t('common.loading')}
        </Text>
      </Stack>
    </Container>
  );
}
